import {
  Modal,
  Text,
  Stack,
  mergeStyles,
  FontWeights,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  PrimaryButton
} from "@fluentui/react";
import { useState, useEffect, useCallback } from "react";

import { useApi, ExecuteData } from "@/core/libs/api/react-query/useApi";
import { cpfCnpjFormatter } from "@/utils/FormatCPForCNPJ";
import { transformToCurrencyString } from "@/utils/TransformToCurrencyString";

interface PaymentData {
  preflightData: PreflightData;
  pixData: PixData;
}

interface PreflightData {
  value: number;
  discountValue: number;
  interestValue: number;
  fineValue: number;
  totalValue: number;
}

interface PixData {
  total: number;
  transactionCode: string;
  paymentCode: string;
  expiresAt: Date;
  expirationInHours: number;
  client: {
    name: string;
    document: string;
  };
}

interface PaymentModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  invoiceIds: number[];
  document: string;
}

const modalStyles = mergeStyles({
  padding: "24px",
  minWidth: "800px",
  maxWidth: "900px"
});

const columnStyles = mergeStyles({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "32px",
  marginTop: "16px"
});

const paymentItemStyles = mergeStyles({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "12px 0",
  borderBottom: "1px solid #e1e1e1"
});

const totalItemStyles = mergeStyles({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 0",
  fontWeight: FontWeights.semibold
});

const paymentMessageContainerStyles = mergeStyles({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px",
  backgroundColor: "#f3f2f1",
  borderRadius: "4px",
  marginTop: "12px"
});

const paymentMessageTextStyles = mergeStyles({
  flex: 1,
  wordBreak: "break-all",
  fontSize: "14px",
  color: "#323130"
});

export default function PaymentModal({
  isOpen,
  onDismiss,
  invoiceIds,
  document
}: PaymentModalProps) {
  const { execute } = useApi();
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preflightData, setPreflightData] = useState<PreflightData>(null);
  const [paymentMessage, setPaymentMessage] = useState<string>("");

  const generatePayment = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data: ExecuteData = {
        method: "POST",
        url: "invoices/payment",
        params: { document },
        bodyObj: { invoiceIds },
        messages: {
          error: "Erro ao gerar pagamento"
        }
      };

      const response = await execute<PaymentData>(data);

      if (response && response.data) {
        const { pixData, preflightData } = response.data;
        setPreflightData(preflightData);

        const memberPortalUrl = import.meta.env.VITE_MEMBER_PORTAL_URL;

        setPaymentMessage(
          `Segue abaixo o link para pagamento de sua fatura. Acesse:\n\n` +
            `${memberPortalUrl}/payments/${pixData.paymentCode}\n\n` +
            `Nome: ${pixData.client.name}\n` +
            `CPF/CNPJ: ${cpfCnpjFormatter(pixData.client.document)}\n\n` +
            `✅ Opções: Pix Copia e Cola ou QR Code.\n` +
            `⏳ Válido por até ${pixData.expirationInHours} horas.\n\n` +
            `Por gentileza, encaminhe o comprovante para baixa após a realização do pagamento. Muito obrigado!`
        );
      } else {
        throw new Error("Dados de pagamento não disponíveis");
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Ocorreu um erro ao gerar o pagamento"
      );
    } finally {
      setLoading(false);
    }
  }, [document, invoiceIds, execute]);

  const resetModalState = useCallback(() => {
    setPreflightData(null);
    setPaymentMessage("");
    setError(null);
    setLoading(false);
    setCopied(false);
  }, []);

  const handleDismiss = useCallback(() => {
    resetModalState();
    onDismiss();
  }, [onDismiss, resetModalState]);

  useEffect(() => {
    if (isOpen && invoiceIds.length > 0 && !preflightData) {
      void generatePayment();
    }
  }, [isOpen, invoiceIds, preflightData, generatePayment]);

  const handleCopyPaymentMessage = async () => {
    if (!paymentMessage) return;

    await navigator.clipboard.writeText(paymentMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={handleDismiss}
      isBlocking={loading}
      containerClassName={modalStyles}
    >
      <Stack>
        {loading && (
          <Stack
            horizontalAlign="center"
            verticalAlign="center"
            style={{ minHeight: 300 }}
          >
            <Spinner size={SpinnerSize.large} label="Gerando pagamento..." />
          </Stack>
        )}

        {error && !loading && (
          <Stack
            horizontalAlign="center"
            verticalAlign="center"
            style={{ minHeight: 200 }}
          >
            <MessageBar
              messageBarType={MessageBarType.error}
              isMultiline={true}
              styles={{ root: { maxWidth: "600px", marginBottom: "20px" } }}
            >
              {error}
            </MessageBar>
            <PrimaryButton
              text="Tentar novamente"
              onClick={void generatePayment}
            />
          </Stack>
        )}

        {!loading && !error && preflightData && (
          <>
            <Stack.Item>
              <div className={columnStyles}>
                <Stack>
                  <Text
                    variant="large"
                    styles={{
                      root: {
                        fontWeight: FontWeights.semibold,
                        marginBottom: "4px"
                      }
                    }}
                  >
                    Resumo do Pagamento
                  </Text>
                  <Text
                    variant="medium"
                    styles={{
                      root: { color: "#605e5c", marginBottom: "16px" }
                    }}
                  >
                    Valores detalhados para o pagamento das faturas
                  </Text>

                  <Stack>
                    <div className={paymentItemStyles}>
                      <Text variant="medium">Valor inicial</Text>
                      <Text
                        variant="medium"
                        styles={{ root: { fontWeight: FontWeights.regular } }}
                      >
                        {transformToCurrencyString(preflightData.value)}
                      </Text>
                    </div>

                    <div className={paymentItemStyles}>
                      <Text variant="medium">Desconto</Text>
                      <Text
                        variant="medium"
                        styles={{
                          root: {
                            fontWeight: FontWeights.regular
                          }
                        }}
                      >
                        {transformToCurrencyString(preflightData.discountValue)}
                      </Text>
                    </div>

                    <div className={paymentItemStyles}>
                      <Text variant="medium">Juros</Text>
                      <Text
                        variant="medium"
                        styles={{
                          root: {
                            fontWeight: FontWeights.regular
                          }
                        }}
                      >
                        {transformToCurrencyString(preflightData.interestValue)}
                      </Text>
                    </div>

                    <div className={paymentItemStyles}>
                      <Text variant="medium">Multa</Text>
                      <Text
                        variant="medium"
                        styles={{
                          root: {
                            fontWeight: FontWeights.regular
                          }
                        }}
                      >
                        {transformToCurrencyString(preflightData.fineValue)}
                      </Text>
                    </div>

                    <div className={totalItemStyles}>
                      <Text variant="mediumPlus">Valor total</Text>
                      <Text
                        variant="mediumPlus"
                        styles={{
                          root: {
                            fontWeight: FontWeights.bold
                          }
                        }}
                      >
                        {transformToCurrencyString(preflightData.totalValue)}
                      </Text>
                    </div>
                  </Stack>
                </Stack>

                <Stack>
                  <Text
                    variant="large"
                    styles={{
                      root: {
                        fontWeight: FontWeights.semibold,
                        marginBottom: "4px"
                      }
                    }}
                  >
                    Informações Para Pagamento
                  </Text>
                  <Text
                    variant="medium"
                    styles={{
                      root: { color: "#605e5c", marginBottom: "16px" }
                    }}
                  >
                    Copie a mensagem abaixo e encaminhe para o cooperado.
                  </Text>

                  {copied && (
                    <MessageBar
                      messageBarType={MessageBarType.success}
                      isMultiline={false}
                    >
                      Mensagem copiada com sucesso!
                    </MessageBar>
                  )}

                  <div className={paymentMessageContainerStyles}>
                    <Text
                      className={paymentMessageTextStyles}
                      style={{ whiteSpace: "pre-line" }}
                    >
                      {paymentMessage}
                    </Text>
                  </div>

                  <PrimaryButton
                    text="Copiar informações"
                    onClick={() => void handleCopyPaymentMessage()}
                    styles={{ root: { marginTop: "16px", width: "100%" } }}
                  />
                </Stack>
              </div>
            </Stack.Item>

            <Stack.Item styles={{ root: { marginTop: "32px" } }}>
              <Stack
                horizontal
                horizontalAlign="end"
                tokens={{ childrenGap: 8 }}
              >
                <PrimaryButton text="Fechar" onClick={handleDismiss} />
              </Stack>
            </Stack.Item>
          </>
        )}
      </Stack>
    </Modal>
  );
}
